import { useEffect, useState } from "react";
import { AlertCircle, Download, FileSignature, PenLine, RefreshCw } from "lucide-react";
import { Badge, Button, Card } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { portal, pdfHref, type PortalContract } from "../../../lib/portal";
import { fmtDate, fmtAmount, genericStatusVariant } from "../../../lib/format";
import { EmptyState, ListSkeleton, Notice, PageHead, RowButton } from "../../ui";

/**
 * Every contract this client may read, the ones still waiting for a signature
 * marked as such. `signable` is the server's answer and is read as it came.
 */
export function ContractList({ onOpen }: { onOpen: (id: string) => void }) {
  const t = useT();
  const { locale } = useI18n();
  const [contracts, setContracts] = useState<PortalContract[] | null>(null);
  const [failed, setFailed] = useState(false);

  function load() {
    setFailed(false);
    setContracts(null);
    void portal.contracts().then(setContracts).catch(() => setFailed(true));
  }
  useEffect(load, []);

  const pending = contracts?.filter((c) => c.signable).length ?? 0;

  return (
    <div>
      <PageHead title={t("portal.contracts.title")} description={t("portal.contracts.lead")} />

      {pending > 0 && (
        <Notice icon={PenLine} className="mb-4">
          {/* One has its own sentence: this app's `t()` has no plural step, and
              "1" in the counted sentence takes the wrong case in Estonian. */}
          {pending === 1 ? t("portal.contracts.pendingBannerOne") : t("portal.contracts.pendingBanner", { count: pending })}
        </Notice>
      )}

      <Card className="overflow-hidden">
        {failed ? (
          <EmptyState
            icon={AlertCircle}
            title={t("portal.loadFailed")}
            action={
              <Button variant="outline" onClick={load}>
                <RefreshCw />
                {t("portal.retry")}
              </Button>
            }
          />
        ) : !contracts ? (
          <ListSkeleton rows={3} label={t("common.loading")} />
        ) : contracts.length === 0 ? (
          <EmptyState icon={FileSignature} title={t("portal.contracts.emptyTitle")} body={t("portal.contracts.emptyBody")} />
        ) : (
          <ul className="divide-y divide-border">
            {contracts.map((c) => {
              const meta = [
                c.number,
                c.valueCents != null ? fmtAmount(c.valueCents, c.currency, locale) : null,
                c.startDate || c.endDate ? `${fmtDate(c.startDate, locale)} - ${fmtDate(c.endDate, locale)}` : null,
              ].filter(Boolean).join(" · ");
              return (
                <li key={c.id} className="flex items-stretch">
                  <RowButton onClick={() => onOpen(c.id)} className="min-w-0 flex-1 flex-col items-stretch gap-1.5 pr-2">
                    <span className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                      <span className="min-w-0 text-[15px] font-semibold">{c.title}</span>
                      {c.signable ? (
                        <Badge>{t("portal.contracts.needsSignature")}</Badge>
                      ) : (
                        <Badge variant={genericStatusVariant(c.status)}>{t(`portal.contractStatus.${c.status}`)}</Badge>
                      )}
                    </span>
                    {meta && <span className="tabular-nums text-foreground/70">{meta}</span>}
                  </RowButton>
                  <Button variant="ghost" size="icon" asChild className="my-auto mr-3 shrink-0">
                    <a
                      href={pdfHref(`/portal/contracts/${c.id}/pdf`)}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t("portal.pdfOf", { number: c.number ?? c.title })}
                      title={t("portal.contracts.downloadPdf")}
                    >
                      <Download />
                    </a>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
